import { useContent } from '@altify/utils';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  useGetPersonalDataQuery,
  useSaveTaxDataMutation,
} from 'store/slices/personalData-api';
import { taxInformationContent } from '../taxInformation.content';
import {
  Button,
  Heading,
  TextDisplayLikeInput,
  Typography,
} from '@altify/components';

import { useGetCountriesQuery } from 'store/slices/references-api';
import { useState } from 'react';
import TaxInformationSuccessModal from '../components/TaxInformationSuccessModal/TaxInformationSuccessModal';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';

export const Route = createFileRoute(
  '/_authenticated/tax-information/review-tax-information',
)({
  component: ReviewTaxInformation,
});

function ReviewTaxInformation() {
  const router = useRouter();
  const localizedContent = useContent(taxInformationContent);
  const {
    data: personalData,
    isLoading: personalDataLoading,
    isFetching: personalDataFetching,
  } = useGetPersonalDataQuery();
  const { data: countries, isLoading: countriesLoading } =
    useGetCountriesQuery();
  const [saveTaxData, saveTaxDataResult] = useSaveTaxDataMutation();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const onSubmit = async () => {
    const result = await saveTaxData({
      addressLine1: personalData?.addressLine1 ?? '',
      addressLine2: personalData?.addressLine2 ?? '',
      city: personalData?.residencyCity ?? '',
      postCode: personalData?.zipCode ?? '',
      countryOfResidence: personalData?.residencyCountryCode ?? '',
      countryOfTaxResidence: personalData?.countryOfTaxResidence ?? '',
      taxIdentificationNumber: personalData?.taxIdentificationNumber ?? '',
    });
    if (!result.error) {
      setShowSuccessModal(true);
    }
  };
  const showLoader =
    personalDataLoading || personalDataFetching || countriesLoading;

  if (showLoader) {
    return <CustomLoader page />;
  }

  return (
    <div className="w-full items-center">
      <div className="flex flex-col w-full items-center flex-1">
        <div className="flex flex-col items-center mb-10">
          <Heading level={4} className="mb-3 mt-5 text-center">
            {localizedContent.reviewDetailsTitle}
          </Heading>
        </div>
        <div className="flex flex-col items-center gap-6 w-full">
          <TextDisplayLikeInput
            label={localizedContent.addressLine1}
            value={personalData?.addressLine1 ?? ''}
            fullWidth={true}
          />
          <TextDisplayLikeInput
            label={localizedContent.addressLine2}
            value={personalData?.addressLine2 ?? ''}
            fullWidth={true}
          />
          <TextDisplayLikeInput
            label={localizedContent.city}
            value={personalData?.residencyCity ?? ''}
            fullWidth={true}
          />
          <TextDisplayLikeInput
            label={localizedContent.postCode}
            value={personalData?.zipCode ?? ''}
            fullWidth={true}
          />
          <TextDisplayLikeInput
            label={localizedContent.countryOfResidence}
            value={
              countries?.find(
                country => country.code === personalData?.residencyCountryCode,
              )?.name ?? ''
            }
            fullWidth={true}
          />
          <TextDisplayLikeInput
            label={localizedContent.countryOfTaxResidence}
            value={
              countries?.find(
                country => country.code === personalData?.countryOfTaxResidence,
              )?.name ?? ''
            }
            fullWidth={true}
          />
          <TextDisplayLikeInput
            label={localizedContent.taxIdentificationNumber}
            value={personalData?.taxIdentificationNumber ?? ''}
            fullWidth={true}
          />
        </div>
      </div>
      <div className="w-full pb-2 pt-10 items-center flex flex-col gap-4">
        {saveTaxDataResult.error && (
          <Typography className="my-5 text-center text-error">
            {localizedContent.unexpectedErrorOccurred}
          </Typography>
        )}
        <Button
          size="large"
          color="secondary"
          onPress={() => {
            router.navigate({
              to: '/tax-information/edit-tax-information',
            });
          }}
          text={localizedContent.editDetailsTitle}
          className="w-full"
        />
        <Button
          size="large"
          color="primary"
          onPress={onSubmit}
          loading={saveTaxDataResult.isLoading}
          text={localizedContent.confirmAndSubmit}
          className="w-full"
        />
      </div>
      <TaxInformationSuccessModal
        isOpen={showSuccessModal}
        isUpdate={false}
        onClose={() => {
          setShowSuccessModal(false);
          router.navigate({ to: '/products' });
        }}
      />
    </div>
  );
}

export default ReviewTaxInformation;
